import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { logApiError } from 'services/apiError'
import NumberInput from 'components/Inputs/NumberInput'
import MoneyInput from 'components/Inputs/MoneyInput'
import DateInput from 'components/Inputs/DateInput'
import Select from 'components/Inputs/Select'
import CheckBox from 'components/CheckBox'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import LoyaltyCard from 'components/Lists/LoyaltyCardList/LoyaltyCard'
import submitOnEnter from 'utils/submitOnEnter'
import loyaltyCardValidator from 'validators/loyaltyCardValidator'
import { FaPlus, FaTrash } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Form,
  FormSection,
  FormField,
  CheckRow,
  Row,
  SubTitle,
  Label,
  WarningText,
  Input
} from './styles'

export default function EditLoyaltyCardModal ({
  visible,
  data: loyaltyCard,
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [loading, setLoading] = useState (true)
  const [services, setServices] = useState ([])
  const token = AuthService.getToken ()
  const businessId = AuthService.getBusinessId ()

  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const serviceOptions = services.map (service => ({
    label: service.nickname || service.name,
    value: service
  }))
  
  const cardTypeOptions = [
    {label: 'Qtd. visitas', value: 'visits'},
    {label: 'Qtd. serviços', value: 'services'},
    {label: 'Valor gasto', value: 'spent'}
  ]

  useEffect (() => {
    getData ()
  }, [visible])

  const getData = async () => {
    try {
      const { data: services } = await cutileiApi.get (
        `/businesses/${businessId}/services?status=active&cutilei_services=false`,
        requestConfig
      )
      setServices (services)
    } catch (error) {
      logApiError (error)
    } finally {
      setLoading (false)
    }
  }

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Editar cartão fidelidade</DialogTitle>
        </DialogHeader>
        <Formik
          validationSchema={loyaltyCardValidator}
          initialValues={{
            'name': loyaltyCard.name,
            'description': loyaltyCard.description,
            'type': cardTypeOptions.find (option => option.value === loyaltyCard.type),
            'requiredVisits': loyaltyCard.required_visits,
            'requiredSpent': loyaltyCard.required_spent,
            'expiresAt': loyaltyCard.expires_at,
            'active': loyaltyCard.active,
            'services': loyaltyCard.services.length > 0 ? loyaltyCard.services.map (service => ({
              id: service.id,
              name: service.name,
              required_amount: service.required_amount
            })) : [{
              id: '', name: '', required_amount: 1
            }],
            'rewards': loyaltyCard.rewards.length > 0 ? loyaltyCard.rewards.map (reward => ({
              service_id: reward.id,
              name: reward.name,
              reward_amount: reward.reward_amount
            })) : [{
              service_id: '', name: '', reward_amount: 1
            }]
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const cardBody = {
                ...values,
                expiresAt: values.expiresAt || null,
                type: values.type.value
              }

              const { data: editedLoyaltyCard } = await cutileiApi.put (
                `/businesses/${businessId}/loyalty_cards/${loyaltyCard.id}`,
                cardBody,
                requestConfig
              )

              setSubmitting (false)
              handleConfirm (editedLoyaltyCard)
              handleClose ()
            } catch (error) {
              setSubmitting (false)
              logApiError (error)
            }
          }}
        >
          {function EditLoyaltyCardForm ({
            values,
            errors,
            dirty,
            touched,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
            handleChange,
            handleBlur,
            handleSubmit
          }) {
            useEffect (() => {
              if (values.type?.value === loyaltyCard.type) return
              if (values.type?.value !== 'services') {
                setFieldValue ('services', [{ id: '', name: '', required_amount: 1 }])
                setFieldValue ('requiredVisits', 1)
                setFieldValue ('requiredSpent', 0)
              }
              if (values.type?.value !== 'visits') {
                setFieldValue ('services', [])
                setFieldValue ('requiredVisits', 1)
                setFieldValue ('requiredSpent', 0)
              }
              if (values.type?.value !== 'spent') {
                setFieldValue ('services', [])
                setFieldValue ('requiredVisits', 1)
                setFieldValue ('requiredSpent', 0)
              }
            }, [dirty, values.type])

            const handleAddService = () => {
              setFieldValue ('services', [
                ...values.services,
                { id: '', name: '', required_amount: 1 }
              ])
            }

            const handleAddReward = () => {
              setFieldValue ('rewards', [
                ...values.rewards,
                { service_id: '', name: '', reward_amount: 1 }
              ])
            }

            const handleDeleteService = index => {
              setFieldValue (
                'services',
                values.services.map ((s, i) => ({...s, idx: i})).filter (s => s.idx !== index)
              )
            }

            const handleDeleteReward = index => {
              setFieldValue (
                'rewards',
                values.rewards.map ((s, i) => ({...s, idx: i})).filter (s => s.idx !== index)
              )
            }

            return (
              <>
                <DialogBody>
                  <Form onKeyDown={submitOnEnter(handleSubmit, isSubmitting)}>
                    <Row>
                      <LoyaltyCard data={{
                        ...values,
                        type: values.type?.value,
                        required_visits: values.requiredVisits,
                        required_spent: values.requiredSpent,
                        expires_at: values.expiresAt
                      }}/>
                    </Row>
                    <CheckRow>
                      <CheckBox
                        id='active'
                        value={values.active}
                        onValueChange={() => setFieldValue ('active', true)}
                      />
                      <Label htmlFor='active'>
                        Ativo
                      </Label>
                    </CheckRow>
                    <CheckRow>
                      <CheckBox
                        id='inactive'
                        value={!values.active}
                        onValueChange={() => setFieldValue ('active', false)}
                      />
                      <Label htmlFor='inactive'>
                        Inativo
                      </Label>
                    </CheckRow>
                    <Row>
                      <FormField stretch={4}>
                        <Label htmlFor='name'>
                          Nome
                        </Label>
                        <Input
                          id='name'
                          placeholder='Nome do cartão'
                          value={values.name}
                          onChange={handleChange ('name')} 
                          onBlur={handleBlur ('name')}
                        />
                        {touched.name && errors.name && (
                          <WarningText>
                            {errors.name}
                          </WarningText>
                        )}
                      </FormField>
                      <FormField stretch={2}>
                        <Label htmlFor='expiresAt'>
                          Data de vencimento
                        </Label>
                        <DateInput
                          name='expiresAt'
                          placeholder='Sem vencimento'
                          clearable={true}
                          value={values.expiresAt}
                          onChange={date => setFieldValue ('expiresAt', date)}
                          onBlur={setFieldTouched}
                          style={{width: '100%'}}
                        />
                        {touched.expiresAt && errors.expiresAt && (
                          <WarningText>
                            {errors.expiresAt}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    <Row>
                      <FormField stretch={4}>
                        <Label htmlFor='description'>
                          Descrição
                        </Label>
                        <Input
                          id='description'
                          placeholder='Descrição do cartão'
                          value={values.description}
                          onChange={handleChange ('description')} 
                          onBlur={handleBlur ('description')}
                        />
                        {touched.description && errors.description && (
                          <WarningText>
                            {errors.description}
                          </WarningText>
                        )}
                      </FormField>
                      <FormField stretch={2}>
                        <Label htmlFor='type'>
                          Tipo de cartão
                        </Label>
                        <Select
                          name='type'
                          placeholder='Selecionar...'
                          value={values.type}
                          options={cardTypeOptions}
                          onChange={setFieldValue}
                          onBlur={setFieldTouched}
                          error={errors.type}
                          touched={touched.type}
                        />
                        {touched.type && errors.type && (
                          <WarningText>
                            {errors.type}
                          </WarningText>
                        )}
                      </FormField>
                    </Row>
                    {values.type?.value === 'services' && (
                      <FormSection>
                        <SubTitle>Serviços a serem concluídos</SubTitle>
                        {values.services.map ((_, index) => (
                          <Row key={index} style={{alignItems: 'center'}}>
                            <FormField stretch={2}>
                              <Select
                                name={`services[${index}].name`}
                                placeholder='Serviço...'
                                value={values.services[index].name ? {
                                  label: values.services[index].name,
                                  value: values.services[index]
                                } : null}
                                options={serviceOptions}
                                onChange={(_, option) => {
                                  setFieldValue (`services[${index}]`, {
                                    ...values.services[index],
                                    id: option.value.id,
                                    name: option.label
                                  })
                                }}
                                onBlur={setFieldTouched}
                                error={errors.services?.[index]?.name}
                                touched={touched.services?.[index]?.name}
                              />
                              {touched.services?.[index]?.name && errors.services?.[index]?.name && (
                                <WarningText>
                                  {errors.services[index].name}
                                </WarningText>
                              )}
                            </FormField>
                            <NumberInput
                              name={`services[${index}].required_amount`}
                              value={values.services[index].required_amount}
                              minValue={1}
                              onChange={setFieldValue}
                            />
                            {values.services.length > 1 && (
                              <MainButton
                                leftIcon={<FaTrash color='#FFFFFF' size={12}/>}
                                onClick={() => handleDeleteService (index)}
                                dangerous={true}
                                width={50}
                              />
                            )}
                          </Row>
                        ))}
                        <SecondaryButton
                          label='Serviço'
                          leftIcon={<FaPlus size={12} color='#FFC100'/>}
                          onClick={handleAddService}
                          width={160}
                        />
                      </FormSection>
                    )}
                    {values.type?.value === 'visits' && (
                      <FormSection>
                        <FormField stretch={1}>
                          <SubTitle>Qtd. visitas necessárias</SubTitle>
                          <NumberInput
                            name='requiredVisits'
                            value={values.requiredVisits}
                            minValue={1}
                            onChange={setFieldValue}
                          />
                        </FormField>
                      </FormSection>
                    )}
                    {values.type?.value === 'spent' && (
                      <FormSection>
                        <FormField stretch={1}>
                          <SubTitle>Valor gasto necessário (R$)</SubTitle>
                          <MoneyInput
                            name='requiredSpent'
                            placeholder='Valor gasto necessário'
                            value={values.requiredSpent}
                            minValue={1}
                            onValueChange={setFieldValue}
                            onBlur={handleBlur ('requiredSpent')}
                            style={{width: '100%'}}
                          />
                          {touched.requiredSpent && errors.requiredSpent && (
                            <WarningText>
                              {errors.requiredSpent}
                            </WarningText>
                          )}
                        </FormField>
                      </FormSection>
                    )}
                    <FormSection>
                      <SubTitle>Recompensas</SubTitle>
                      {values.rewards.map ((_, index) => (
                        <Row key={index}>
                          <FormField stretch={2}>
                            <Select
                              name={`rewards[${index}].name`}
                              placeholder='Recompensa...'
                              value={values.rewards[index].name ? {
                                label: values.rewards[index].name,
                                value: values.rewards[index]
                              } : null}
                              options={serviceOptions}
                              onChange={(_, option) => {
                                setFieldValue (`rewards[${index}]`, {
                                  ...values.rewards[index],
                                  service_id: option.value.id,
                                  name: option.label
                                })
                              }}
                              onBlur={setFieldTouched}
                              error={errors.rewards?.[index]?.name}
                              touched={touched.rewards?.[index]?.name}
                            />
                            {touched.rewards?.[index]?.name && errors.rewards?.[index]?.name && (
                              <WarningText>
                                {errors.rewards[index].name}
                              </WarningText>
                            )}
                          </FormField>
                          <NumberInput
                            name={`rewards[${index}].reward_amount`}
                            value={values.rewards[index].reward_amount}
                            minValue={1}
                            onChange={setFieldValue}
                          />
                          {values.rewards.length > 1 && (
                            <MainButton
                              leftIcon={<FaTrash color='#FFFFFF' size={12}/>}
                              onClick={() => handleDeleteReward (index)}
                              dangerous={true}
                              width={50}
                            />
                          )}
                        </Row>
                      ))}
                      <SecondaryButton
                        label='Recompensa'
                        leftIcon={<FaPlus size={12} color='#FFC100'/>}
                        onClick={handleAddReward}
                        width={160}
                      />
                    </FormSection>
                  </Form>
                </DialogBody>
                <DialogFooter>
                  <MainButton
                    label='Salvar alterações'
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    width={180}
                    style={{marginTop: 10}}
                  />
                </DialogFooter>
              </>
            ) 
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
